import { Emitter, isHlsUrl } from './utils.js';
import { device } from './device.js';

/* Núcleo de reproducción: abstrae hls.js y el <video> nativo con una API única.
   - HLS: hls.js cuando hay MSE (Chrome, Android, Electron, TVs); nativo en Safari/iOS.
   - Ficheros (mp4/mkv/...): <video> nativo con soporte de Range para buscar.
   - Recuperación automática de errores de red/media con reintentos escalonados. */

/* hls.js (620 KB) se carga bajo demanda la primera vez que hace falta un stream HLS, en lugar
   de al arrancar la app: el inicio es más rápido, sobre todo en móviles y TV boxes. */
let hlsLoading = null;
export function ensureHls() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLoading) return hlsLoading;
    hlsLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'vendor/hls/hls.min.js';
        s.async = true;
        s.onload = () => resolve(window.Hls);
        s.onerror = () => { hlsLoading = null; reject(new Error('hls.js')); };
        document.head.appendChild(s);
    });
    return hlsLoading;
}

export class PlayerCore extends Emitter {
    constructor(video) {
        super();
        this.video = video;
        this.hls = null;
        this.url = null;
        this.isLive = false;
        this.retries = 0;
        this._destroyed = false;
        this._bindVideo();
    }

    _bindVideo() {
        const v = this.video;
        const relay = (name, mapped) => v.addEventListener(name, (e) => this.emit(mapped || name, e));
        ['play', 'pause', 'playing', 'waiting', 'timeupdate', 'ended', 'volumechange', 'ratechange', 'durationchange', 'loadedmetadata', 'progress', 'seeking', 'seeked', 'canplay'].forEach((n) => relay(n));
        v.addEventListener('error', () => {
            if (!v.src && !this.hls) return;
            const code = v.error ? v.error.code : 0;
            const message = v.error && v.error.message;
            const fail = () => this.emit('error', { type: code === 4 || code === 3 ? 'media' : 'network', code, fatal: true, message });
            // 3 = decode, 4 = src not supported. Sin hls.js ni transcodificación, una dirección que no dice ser HLS
            // pero lo es se reintenta con hls.js antes de dar el error.
            if ((code === 3 || code === 4) && !this.hls && !this.transcode && !this._sniffed && this.url && !(device.isIOS && device.nativeHls)) {
                this._sniffed = true;
                const url = this.url;
                this._looksLikeHls(url).then(async (yes) => {
                    if (!yes || this.url !== url || this._destroyed) return fail();
                    try { await ensureHls(); } catch (e) { return fail(); }
                    if (!this.supportsHlsJs() || this.url !== url || this._destroyed) return fail();
                    const a = this._loadArgs || {};
                    this._startHls(url, !!a.live, a.startPosition || 0);
                    if (a.autoplay !== false) v.play().catch(() => this.emit('autoplayblocked'));
                });
                return;
            }
            fail();
        });
    }

    supportsHlsJs() {
        return !!(window.Hls && window.Hls.isSupported());
    }

    engineName() {
        if (this.hls) return 'hls.js';
        if (this.transcode) return 'native · audio→AAC';
        return 'native';
    }

    get isTranscoding() {
        return !!this.transcode;
    }

    /** Salto en flujo convertido: si el destino ya está en búfer se mueve el <video>; si no, se vuelve a pedir desde ahí. */
    _seekTranscoded(time) {
        const tr = this.transcode;
        const v = this.video;
        const local = time - tr.offset;
        // El flujo no admite Range: solo se puede mover el <video> dentro de lo que el navegador
        // declara buscable (lo ya recibido); en otro caso se vuelve a pedir desde el destino.
        const s = v.seekable;
        if (local >= 0 && s && s.length) {
            for (let i = 0; i < s.length; i++) {
                if (s.start(i) <= local && s.end(i) >= local) {
                    try { v.currentTime = local; } catch (e) { /* ignore */ }
                    return;
                }
            }
        }
        const wasPaused = v.paused;
        tr.offset = Math.max(0, Math.floor(time));
        this.url = tr.urlFor(tr.offset);
        this.emit('seeking');
        v.src = this.url;
        v.load();
        if (!wasPaused) v.play().catch(() => {});
        const once = () => { v.removeEventListener('loadeddata', once); this.emit('seeked'); };
        v.addEventListener('loadeddata', once);
    }

    /**
     * @param {object} o
     * @param {string} o.url
     * @param {{ urlFor: (t:number) => string, duration: number }} [o.transcode]
     *   Flujo convertido en el servidor: sin Range, cada salto vuelve a pedir la URL desde ese
     *   segundo (urlFor). El tiempo real es offset + tiempo del <video>.
     */
    async load({ url, live = false, startPosition = 0, muted = false, autoplay = true, transcode = null }) {
        this.unload();
        this.url = url;
        this.isLive = live;
        this.retries = 0;
        this._sniffed = false;
        this._loadArgs = { live, startPosition, autoplay };
        const v = this.video;
        v.muted = muted;
        const token = (this._loadToken = (this._loadToken || 0) + 1);

        if (transcode) {
            this.transcode = { urlFor: transcode.urlFor, duration: transcode.duration || 0, offset: Math.max(0, startPosition || 0) };
            this.url = transcode.urlFor(this.transcode.offset);
            v.src = this.url;
            v.load();
            if (autoplay) {
                try {
                    await v.play();
                } catch (err) {
                    this.emit('autoplayblocked', err);
                }
            }
            return;
        }

        if (isHlsUrl(url) && !(device.isIOS && device.nativeHls) && !window.Hls) {
            try { await ensureHls(); } catch (e) { /* sin hls.js: se intenta en nativo */ }
            if (token !== this._loadToken) return; // otra carga empezó mientras tanto
        }
        const useHlsJs = isHlsUrl(url) && this.supportsHlsJs() && !(device.isIOS && device.nativeHls);

        if (useHlsJs) {
            this._startHls(url, live, startPosition);
        } else {
            v.src = url;
            if (startPosition > 0) {
                const seekOnce = () => {
                    v.removeEventListener('loadedmetadata', seekOnce);
                    try {
                        v.currentTime = startPosition;
                    } catch (e) { /* ignore */ }
                };
                v.addEventListener('loadedmetadata', seekOnce);
            }
            v.load();
        }

        if (autoplay) {
            try {
                await v.play();
            } catch (err) {
                // Autoplay bloqueado: la UI muestra el botón de play grande.
                this.emit('autoplayblocked', err);
            }
        }
    }

    /** Reproduce `url` con hls.js. */
    _startHls(url, live, startPosition) {
        const v = this.video;
        const Hls = window.Hls;
        const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
            backBufferLength: live ? 30 : 90,
            // Más colchón en la tele (WiFi flojo, pocas interacciones); en el móvil menos para arrancar antes.
            maxBufferLength: live ? (device.isTV ? 30 : 20) : (device.isTV ? 90 : 60),
            maxMaxBufferLength: device.isTV ? 180 : 120,
            // Con varias calidades dentro del stream: empezar prudente (sube solo en segundos) y no bajar más resolución de la que cabe en pantalla.
            abrEwmaDefaultEstimate: 2500000,
            capLevelToPlayerSize: true,
            liveSyncDurationCount: 3,
            startPosition: live ? -1 : (startPosition || -1),
            startFragPrefetch: true, // el primer fragmento se pide en cuanto se conoce la lista, sin esperar al vídeo
            manifestLoadingMaxRetry: 3,
            levelLoadingMaxRetry: 4,
            fragLoadingMaxRetry: 6,
            fragLoadingRetryDelay: 1000,
            xhrSetup: (xhr) => {
                xhr.withCredentials = false;
            }
        });
        this.hls = hls;
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
            this.emit('levels', this.levels());
            this.emit('tracks');
        });
        hls.on(Hls.Events.LEVEL_SWITCHED, () => this.emit('levelchange', this.currentLevel()));
        hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => this.emit('tracks'));
        hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, () => this.emit('tracks'));
        hls.on(Hls.Events.ERROR, (event, data) => this._onHlsError(data));
        hls.loadSource(url);
        hls.attachMedia(v);
    }

    /**
     * Una dirección sin «.m3u8» puede ser igualmente un manifiesto HLS (pasa con listas públicas y con
     * servicios que sirven el vídeo desde rutas opacas). Al fallar en nativo se mira si empieza por #EXTM3U.
     */
    async _looksLikeHls(url) {
        try {
            const r = await fetch(url, { headers: { Range: 'bytes=0-511' }, cache: 'no-store', signal: AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined });
            if (!r.ok && r.status !== 206) return false;
            const text = (await r.text()).slice(0, 512);
            return /^\s*#EXTM3U/.test(text);
        } catch (e) {
            return false;
        }
    }

    _onHlsError(data) {
        const Hls = window.Hls;
        if (!data.fatal) {
            if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) this.emit('waiting');
            return;
        }
        if (this._destroyed || !this.hls) return;
        switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                if (this.retries < 4) {
                    this.retries++;
                    this.emit('retrying', this.retries);
                    setTimeout(() => { if (this.hls) this.hls.startLoad(); }, 800 * this.retries);
                } else {
                    this.emit('error', { type: 'network', fatal: true, details: data.details, status: data.response && data.response.code });
                }
                break;
            case Hls.ErrorTypes.MEDIA_ERROR:
                if (this.retries < 3) {
                    this.retries++;
                    this.emit('retrying', this.retries);
                    this.hls.recoverMediaError();
                } else {
                    this.emit('error', { type: 'media', fatal: true, details: data.details });
                }
                break;
            default:
                this.emit('error', { type: 'generic', fatal: true, details: data.details });
        }
    }

    unload() {
        if (this.hls) {
            try {
                this.hls.destroy();
            } catch (e) { /* ignore */ }
            this.hls = null;
        }
        const v = this.video;
        try {
            v.pause();
            v.removeAttribute('src');
            v.load();
        } catch (e) { /* ignore */ }
        this.url = null;
        this.transcode = null;
    }

    destroy() {
        this._destroyed = true;
        this.unload();
    }

    play() {
        return this.video.play().catch(() => {});
    }

    pause() {
        this.video.pause();
    }

    toggle() {
        if (this.video.paused) return this.play();
        this.pause();
    }

    get paused() {
        return this.video.paused;
    }

    get currentTime() {
        const t = this.video.currentTime || 0;
        return this.transcode ? this.transcode.offset + t : t;
    }

    get duration() {
        if (this.transcode && this.transcode.duration) return this.transcode.duration;
        const d = this.video.duration;
        return isFinite(d) ? d : 0;
    }

    seek(time) {
        if (this.transcode) return this._seekTranscoded(Math.max(0, Math.min(this.duration || time, time)));
        const range = this.seekRange();
        const t = Math.max(range.start, Math.min(range.end, time));
        try {
            this.video.currentTime = t;
        } catch (e) { /* ignore */ }
    }

    seekBy(delta) {
        this.seek(this.currentTime + delta);
    }

    /** Rango buscable (DVR en directo). */
    seekRange() {
        const v = this.video;
        if (this.transcode) return { start: 0, end: this.duration };
        if (this.hls && this.hls.liveSyncPosition != null && this.isLive) {
            const s = v.seekable;
            if (s && s.length) return { start: s.start(0), end: s.end(s.length - 1) };
        }
        const s = v.seekable;
        if (s && s.length) return { start: s.start(0), end: s.end(s.length - 1) };
        return { start: 0, end: this.duration };
    }

    dvrWindow() {
        if (!this.isLive) return 0;
        const r = this.seekRange();
        return Math.max(0, r.end - r.start);
    }

    isAtLiveEdge() {
        if (!this.isLive) return false;
        const r = this.seekRange();
        return r.end - this.currentTime < 12;
    }

    goLive() {
        if (!this.isLive) return;
        const r = this.seekRange();
        this.seek(Math.max(r.start, r.end - 3));
        this.play();
    }

    buffered() {
        const v = this.video;
        const b = v.buffered;
        if (!b || !b.length) return 0;
        const t = v.currentTime;
        const offset = this.transcode ? this.transcode.offset : 0;
        for (let i = 0; i < b.length; i++) {
            if (b.start(i) <= t && b.end(i) >= t) return offset + b.end(i);
        }
        return offset + b.end(b.length - 1);
    }

    set volume(value) {
        this.video.volume = Math.max(0, Math.min(1, value));
    }

    get volume() {
        return this.video.volume;
    }

    set muted(value) {
        this.video.muted = !!value;
    }

    get muted() {
        return this.video.muted;
    }

    set playbackRate(value) {
        this.video.playbackRate = value;
    }

    get playbackRate() {
        return this.video.playbackRate;
    }

    /* ----- Calidad ----- */

    /** Resumen técnico para el mensaje de error (motor, códecs, host del stream). */
    diagnostics() {
        const out = { engine: this.engineName(), live: this.isLive };
        try { out.host = new URL(this.url, location.href).host; } catch (e) { /* sin url */ }
        if (this.hls && this.hls.levels && this.hls.levels.length) {
            const l = this.hls.levels[Math.max(0, this.hls.currentLevel)] || this.hls.levels[0];
            out.video = l.videoCodec || '';
            out.audio = l.audioCodec || '';
            out.size = l.width && l.height ? l.width + 'x' + l.height : '';
        }
        const v = this.video;
        out.readyState = v.readyState;
        out.networkState = v.networkState;
        if (v.error) out.mediaError = v.error.code + (v.error.message ? ' ' + v.error.message : '');
        return out;
    }

    /** Métricas en vivo para la capa de estadísticas. */
    stats() {
        const v = this.video;
        const out = { engine: this.engineName(), buffer: 0, dropped: 0, frames: 0, bandwidth: 0, bitrate: 0, latency: null, size: v.videoWidth && v.videoHeight ? v.videoWidth + 'x' + v.videoHeight : '' };
        try {
            for (let i = 0; i < v.buffered.length; i++) {
                if (v.buffered.start(i) <= v.currentTime && v.buffered.end(i) >= v.currentTime) out.buffer = v.buffered.end(i) - v.currentTime;
            }
        } catch (e) { /* sin buffered */ }
        if (v.getVideoPlaybackQuality) {
            const q = v.getVideoPlaybackQuality();
            out.dropped = q.droppedVideoFrames;
            out.frames = q.totalVideoFrames;
        }
        if (this.hls) {
            out.bandwidth = Math.round((this.hls.bandwidthEstimate || 0) / 1000);
            const l = this.hls.levels && this.hls.levels[Math.max(0, this.hls.currentLevel)];
            if (l) {
                out.bitrate = Math.round((l.bitrate || 0) / 1000);
                out.codec = [l.videoCodec, l.audioCodec].filter(Boolean).join(', ');
            }
            if (typeof this.hls.latency === 'number' && isFinite(this.hls.latency)) out.latency = this.hls.latency;
        }
        return out;
    }

    /** Caudal estimado de la red (kb/s) según lo que va descargando; 0 si no se sabe. */
    bandwidthKbps() {
        return this.hls && this.hls.bandwidthEstimate > 0 && isFinite(this.hls.bandwidthEstimate) ? Math.round(this.hls.bandwidthEstimate / 1000) : 0;
    }

    levels() {
        if (!this.hls) return [];
        return this.hls.levels.map((l, i) => ({
            index: i,
            height: l.height,
            width: l.width,
            bitrate: l.bitrate,
            label: l.height ? l.height + 'p' : Math.round(l.bitrate / 1000) + ' kbps'
        }));
    }

    currentLevel() {
        if (!this.hls) return null;
        const l = this.hls.levels[this.hls.currentLevel];
        return l ? { index: this.hls.currentLevel, height: l.height, bitrate: l.bitrate, auto: this.hls.autoLevelEnabled } : null;
    }

    setLevel(index) {
        if (!this.hls) return;
        this.hls.currentLevel = index; // -1 = auto
    }

    /* ----- Pistas de audio / subtítulos ----- */

    audioTracks() {
        if (this.hls) {
            return this.hls.audioTracks.map((t, i) => ({ index: i, label: t.name || t.lang || ('Pista ' + (i + 1)), lang: t.lang, active: i === this.hls.audioTrack }));
        }
        const tracks = this.video.audioTracks;
        if (!tracks) return [];
        const out = [];
        for (let i = 0; i < tracks.length; i++) {
            out.push({ index: i, label: tracks[i].label || tracks[i].language || ('Pista ' + (i + 1)), lang: tracks[i].language, active: tracks[i].enabled });
        }
        return out;
    }

    setAudioTrack(index) {
        if (this.hls) {
            this.hls.audioTrack = index;
            return;
        }
        const tracks = this.video.audioTracks;
        if (!tracks) return;
        for (let i = 0; i < tracks.length; i++) tracks[i].enabled = i === index;
    }

    subtitleTracks() {
        if (this.hls) {
            return this.hls.subtitleTracks.map((t, i) => ({ index: i, label: t.name || t.lang || ('Subtítulos ' + (i + 1)), lang: t.lang, active: i === this.hls.subtitleTrack }));
        }
        const tracks = this.video.textTracks;
        if (!tracks) return [];
        const out = [];
        for (let i = 0; i < tracks.length; i++) {
            if (tracks[i].kind !== 'subtitles' && tracks[i].kind !== 'captions') continue;
            out.push({ index: i, label: tracks[i].label || tracks[i].language || ('Subtítulos ' + (i + 1)), lang: tracks[i].language, active: tracks[i].mode === 'showing' });
        }
        return out;
    }

    setSubtitleTrack(index) {
        if (this.hls) {
            this.hls.subtitleTrack = index;
            this.hls.subtitleDisplay = index >= 0;
            return;
        }
        const tracks = this.video.textTracks;
        if (!tracks) return;
        for (let i = 0; i < tracks.length; i++) tracks[i].mode = i === index ? 'showing' : 'disabled';
    }
}
